import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const HabitIconSuggestionStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  STALE: 'stale',
};

const useHabitIconSuggestion = ({
  debounceMs,
  existingIconId = null,
  initialName = '',
  name,
  onSuggestionUnavailable,
  suggestIcon,
  usedIconIds,
}) => {
  const [iconId, setIconId] = useState(existingIconId);
  const [hasManualIconSelection, setHasManualIconSelection] = useState(Boolean(existingIconId));
  const [lastFetchedName, setLastFetchedName] = useState(null);
  const [lastSuggestedName, setLastSuggestedName] = useState(existingIconId ? initialName.trim() : null);

  const latestRequestIdRef = useRef(0);
  const trimmedName = useMemo(() => (name || '').trim(), [name]);

  const invalidatePendingSuggestions = useCallback(() => {
    latestRequestIdRef.current += 1;
  }, []);

  useEffect(() => {
    if (hasManualIconSelection) {
      return undefined;
    }

    if (!trimmedName) {
      setLastFetchedName(null);
      if (!existingIconId) {
        setIconId(null);
        setLastSuggestedName(null);
      }
      return undefined;
    }

    if (lastSuggestedName && trimmedName !== lastSuggestedName) {
      setLastSuggestedName(null);
    }

    if (lastFetchedName === trimmedName) {
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setLastFetchedName(trimmedName);

      try {
        const suggestedIconId = await suggestIcon(trimmedName, usedIconIds);
        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        if (suggestedIconId) {
          setIconId(suggestedIconId);
          setLastSuggestedName(trimmedName);
          return;
        }

        setIconId(null);
        setLastSuggestedName(null);
        onSuggestionUnavailable();
      } catch {
        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        setIconId(null);
        setLastSuggestedName(null);
        onSuggestionUnavailable();
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [
    debounceMs,
    existingIconId,
    hasManualIconSelection,
    lastFetchedName,
    lastSuggestedName,
    onSuggestionUnavailable,
    suggestIcon,
    trimmedName,
    usedIconIds,
  ]);

  const handleNameChangeStart = useCallback(() => {
    invalidatePendingSuggestions();
    if (!hasManualIconSelection) {
      setLastFetchedName(null);
      setLastSuggestedName(null);
    }
  }, [hasManualIconSelection, invalidatePendingSuggestions]);

  const handleManualIconChange = useCallback((id) => {
    invalidatePendingSuggestions();
    setIconId(id);
    setHasManualIconSelection(true);
  }, [invalidatePendingSuggestions]);

  const suggestionStatus = useMemo(() => {
    if (hasManualIconSelection) {
      return HabitIconSuggestionStatus.IDLE;
    }

    if (!trimmedName) {
      return HabitIconSuggestionStatus.IDLE;
    }

    if (lastFetchedName !== trimmedName) {
      return HabitIconSuggestionStatus.STALE;
    }

    if (lastSuggestedName !== trimmedName) {
      return HabitIconSuggestionStatus.LOADING;
    }

    return HabitIconSuggestionStatus.IDLE;
  }, [hasManualIconSelection, lastFetchedName, lastSuggestedName, trimmedName]);

  return {
    handleManualIconChange,
    handleNameChangeStart,
    iconId,
    suggestionStatus,
  };
};

export {
  HabitIconSuggestionStatus,
};

export default useHabitIconSuggestion;
